import _ from 'lodash';
import React, { useState } from 'react';
import { makeStyles } from 'react-scale/components/design-system';
import { Button } from 'design-system/Button';
import { Radio } from 'design-system/Radio';
import { Dialog } from 'design-system';
import Textarea from 'design-system/Textarea';
import Backdrop from '@mui/material/Backdrop';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ReportSensitiveContentTaskModalProps {
  handleSubmit: (reason: string, explanation: string) => void;
  isModalOpen: boolean;
  onCloseModal: () => void;
  categories: Record<string, string>;
  tooltips: Record<string, string>;
  confirmButtonText: string;
  bodyText: string;
  headerText: string;
  explanationPlaceholder: string;
}

const highZIndex = 100002;

const ReportSensitiveContentTaskModal = (props: ReportSensitiveContentTaskModalProps) => {
  const styles = useStyles();

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [explanation, setExplanation] = useState<string>('');

  const closeModal = () => {
    setSelectedCategory('');
    setExplanation('');
    props.onCloseModal();
  };

  const renderCategoryInput = () => {
    return (
      <div className={styles.category}>
        <div>
          {_.keys(props.categories).map(category => {
            const displayText = props.categories[category];
            const tooltipText = props.tooltips[category];
            if (!displayText || !tooltipText) {
              return null;
            }
            return (
              <div key={category}>
                <div onClick={() => setSelectedCategory(category)}>
                  <label>
                    <Radio
                      value={category}
                      label={displayText}
                      checked={category === selectedCategory}
                    />{' '}
                    <FontAwesomeIcon
                      data-tip
                      data-for={category}
                      icon="info-circle"
                      color="lightgrey"
                    />
                    <ReactTooltip id={category} place="right" effect="solid">
                      {tooltipText}
                    </ReactTooltip>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Backdrop
      open={props.isModalOpen}
      sx={{
        backdropFilter: 'blur(5px)',
        zIndex: highZIndex,
      }}
    >
      <Dialog
        open={props.isModalOpen}
        onClose={closeModal}
        maxWidth={'md'}
        style={{ zIndex: highZIndex + 1 }}
      >
        <div className={styles.reportTask}>
          <div className={styles.intro}>
            <h2>{props.headerText}</h2>
            <p>{props.bodyText}</p>
          </div>

          <div className={styles.section}>
            <div className="label">Select a reason (required)</div>
            {renderCategoryInput()}
          </div>

          <div className={styles.section}>
            {selectedCategory === 'other_harmful_content' ? (
              <div className="label">Explain your reason (required)</div>
            ) : (
              <div className="label">Explain your reason (optional)</div>
            )}
            <Textarea
              onChange={e => setExplanation(e.target.value)}
              value={explanation}
              placeholder={props.explanationPlaceholder}
            ></Textarea>
          </div>

          <div>
            {(selectedCategory !== 'other_harmful_content' || explanation) &&
              selectedCategory !== '' && (
                <div
                  className={styles.submit}
                  onClick={() => props.handleSubmit(selectedCategory, explanation)}
                >
                  <Button>{props.confirmButtonText}</Button>
                </div>
              )}
          </div>
        </div>
      </Dialog>
    </Backdrop>
  );
};

const useStyles = makeStyles({
  category: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 14,
    fontWeight: 'bold',
  },
  reportTask: {
    padding: 32,
  },
  intro: {
    fontSize: 16,
    '& h2': {
      margin: 0,
      fontSize: 20,
      fontWeight: 'bold',
    },
    '& p': {
      margin: '4px 0 32px',
      lineHeight: 1.7,
    },
  },
  section: {
    margin: '36px 0',
    '& .label': {
      marginBottom: 8,
      textTransform: 'uppercase',
      fontWeight: 'bold',
      fontSize: 13,
      color: 'rgba(0, 0, 0, 0.25)',
      letterSpacing: 0.5,
    },
  },
  submit: {
    display: 'flex',
    justifyContent: 'center',
  },
});

export default ReportSensitiveContentTaskModal;
